import { useCallback, useContext, useDeferredValue } from 'react';

import type { PermissionName } from '@models';
import checkRoutePermission from '@routes/check-route-permission';
import type { RouteMetadata } from '@routes/types';
import { useAppSelector } from '@store/hooks';
import type { UseDeferredStateOptions } from '@hooks/types';

import {
  PermissionsContext,
  useGetNavPermissions,
} from './permissions-provider';

/**
 * Returns the current state from Permissions Context. It may update sooner than
 * you expect.
 *
 * In most cases, you should prefer `usePermissions` instead, which returns a
 * deferred state.
 *
 * @see usePermissions
 */
export const usePermissionsContext = () => useContext(PermissionsContext);

export type UsePermissionsOptions = UseDeferredStateOptions;

/**
 * Returns a stable state from Permissions Context. Updates are deferred to the
 * next render cycle, allowing priority updates to settle first - Primarily
 * side effects and transitions during route resolution.
 *
 * If you need the eager state, pass `deferred: false` to the options.
 */
export const usePermissions = ({
  deferred = true,
}: UsePermissionsOptions = {}) => {
  const context = usePermissionsContext();
  const deferredContext = useDeferredValue(context);

  return deferred ? deferredContext : context;
};

/**
 * Returns true if the user has one of the permissions passed in
 */
export const useHasAnyPermission = (...oneOfPermissions: PermissionName[]) => {
  const { permissions } = usePermissions();

  return oneOfPermissions.some(
    permission => permissions?.[permission] === true,
  );
};

/**
 * Returns true if the user has all of the permissions passed in
 */
export const useHasAllPermissions = (...allOfPermissions: PermissionName[]) => {
  const { permissions } = usePermissions();

  return allOfPermissions.every(
    permission => permissions?.[permission] === true,
  );
};

export const useCheckRoutePermission = () => {
  const account = useAppSelector(state => state.auth.account);

  const getNavPermissions = useGetNavPermissions(account);

  return useCallback(
    (route: RouteMetadata) => {
      const permissions = getNavPermissions(route);

      return checkRoutePermission({ route, permissions });
    },
    [getNavPermissions],
  );
};

export default usePermissions;
